import { DSAttendanceStatus } from '@/constants/ds-attendance.constants'
import PATHS from '@/router/paths'
import { approvalsQueueLocation } from '@/modules/company/Approvals/Hub/queues'
import type { PostStationDomainOrigin } from '@/Network/Types/Responses/PostStationResponses'

import type { PostStationCompliance, PostStationRow } from './types'

export interface PostStationResolvedRoute {
  path: string
  query?: Record<string, string>
}

const dsAttendanceAuditPath = (resourceId: number): string =>
  PATHS.DS_ATTENDANCE_PENDING_AUDIT.replace(':attendanceId', String(resourceId))

const dsAttendanceSubmissionPath = (resourceId: number): string =>
  PATHS.DS_ATTENDANCE_PENDING_SUBMISSION.replace(':attendanceId', String(resourceId))

const dsAttendanceApprovedPath = (resourceId: number): string =>
  PATHS.DS_ATTENDANCE_APPROVED.replace(':attendanceId', String(resourceId))

/**
 * Rota do container de registro de plantão (ds-attendance) segundo o status
 * atual do container. `PENDING_AUDIT`/`AUDIT_IN_PROGRESS`/`REJECTED` (e status
 * ausente/desconhecido, ex.: backend antigo sem o campo) caem na tela de
 * auditoria pendente — o mesmo destino usado antes deste status existir.
 */
export const dsAttendancePathForStatus = (resourceId: number, status?: string | null): string => {
  switch (status) {
    case DSAttendanceStatus.APPROVED:
      return dsAttendanceApprovedPath(resourceId)
    case DSAttendanceStatus.PENDING_SUBMISSION:
      return dsAttendanceSubmissionPath(resourceId)
    default:
      return dsAttendanceAuditPath(resourceId)
  }
}

/**
 * CTA "Abrir módulo de origem": rota do módulo onde a pendência do domínio é
 * tratada. `origin.resourceId` nulo cai na lista do módulo; excepcionalidade
 * filtra a auditoria pela escala da linha; pagamento não tem recurso pontual.
 */
export const resolveOriginRoute = (
  domainKey: keyof PostStationCompliance,
  origin: PostStationDomainOrigin | null,
  row: Pick<PostStationRow, 'scheduleId'>
): PostStationResolvedRoute => {
  const resourceId = origin?.resourceId ?? null

  switch (domainKey) {
    case 'registration':
      return resourceId !== null
        ? { path: dsAttendancePathForStatus(resourceId, origin?.status) }
        : approvalsQueueLocation('attendance', { status: 'PENDING_AUDIT' })
    case 'qualification':
      return resourceId !== null
        ? { path: `${PATHS.MANAGE_QUALIFICATION_DOC}/${resourceId}` }
        : {
            path: PATHS.MANAGE_SCALE,
            query: {
              openScheduleId: String(row.scheduleId),
              editTab: 'qualificacao-documental'
            }
          }
    case 'repass':
      return resourceId !== null
        ? { path: `${PATHS.MANAGE_QUALIFICATION_TRANSFER}/${resourceId}` }
        : {
            path: PATHS.MANAGE_SCALE,
            query: {
              openScheduleId: String(row.scheduleId),
              editTab: 'qualificacao-repasse'
            }
          }
    case 'exception':
      return {
        path: PATHS.AUDITORIUM_EXCEPTIONALITIES,
        // O módulo parseia `scheduleIds` como lista separada por vírgula.
        query: { scheduleIds: String(row.scheduleId) }
      }
    case 'payment':
      return { path: PATHS.MANAGE_SCALE }
  }
}

/**
 * "Abrir plantão": deep-link no formato que o `onMounted` de `Dashboard/Index.vue`
 * parseia — `startDate` (normalizado lá para a semana do plantão), `schedules`
 * (valor único vira array no parse) e `stationId` (abre o modal de detalhes).
 */
export const resolveOpenStationRoute = (
  row: Pick<PostStationRow, 'stationId' | 'scheduleId' | 'dateIso'>
): PostStationResolvedRoute => ({
  path: PATHS.DASHBOARD,
  query: {
    startDate: row.dateIso,
    schedules: String(row.scheduleId),
    stationId: String(row.stationId)
  }
})
